import React, { useMemo } from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme';
import {
  Heading,
  Table,
  TableColumnConfig,
  Text,
  CheckIcon,
  XIcon,
} from '@weave/design-system';
import { PayerMixDetail } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';

type PayerMixDetailsTableProps = {
  payerMixDetails?: PayerMixDetail[];
};

// Sort function to prioritize web supported + not connected at top
const sortPayerMixDetails = (details: PayerMixDetail[]): PayerMixDetail[] => {
  return [...details].sort((a, b) => {
    // Priority 1: Web supported AND not connected (highest priority)
    const aWebSupportedNotConnected = a.webSupported && !a.connected;
    const bWebSupportedNotConnected = b.webSupported && !b.connected;

    if (aWebSupportedNotConnected && !bWebSupportedNotConnected) return -1;
    if (!aWebSupportedNotConnected && bWebSupportedNotConnected) return 1;

    // Priority 2: Sort by appointment count (descending)
    return (b.appointmentCount ?? 0) - (a.appointmentCount ?? 0);
  });
};

export const PayerMixDetailsTable = ({ payerMixDetails }: PayerMixDetailsTableProps) => {
  if (!payerMixDetails || payerMixDetails.length === 0) {
    return null;
  }

  const sortedPayerMixDetails = useMemo(
    () => sortPayerMixDetails(payerMixDetails),
    [payerMixDetails]
  );

  const columns = useMemo(
    (): TableColumnConfig<PayerMixDetail>[] => [
      {
        id: 'payerName',
        Header: 'Payer Name',
        accessor: (data) => data.payerName ?? '',
        minWidth: 350,
        maxWidth: 900,
      },
      {
        id: 'payerId',
        Header: 'Payer ID',
        accessor: (data) => data.payerId ?? '',
        maxWidth: 120,
      },
      {
        id: 'appointments',
        Header: 'Appointments',
        accessor: (data) =>
          `${data.appointmentCount ?? 0} (${data.appointmentPercentage ?? 0}%)`,
        maxWidth: 150,
      },
      {
        id: 'webSupported',
        Header: 'Web Supported',
        accessor: (data) => (data.webSupported ? 'Yes' : 'No'),
        cellRenderer: (cellValue, row) => (
          <div css={statusCellStyles}>
            {row?.webSupported ? (
              <span css={statusBadgeStyles('success')}>
                <CheckIcon size={14} />
                Yes
              </span>
            ) : (
              <span css={statusBadgeStyles('neutral')}>
                <XIcon size={14} />
                No
              </span>
            )}
          </div>
        ),
        maxWidth: 200,
      },
      {
        id: 'connected',
        Header: 'Connected',
        accessor: (data) => (!data.webSupported ? '-' : data.connected ? 'Yes' : 'No'),
        cellRenderer: (cellValue, row) => (
          <div css={statusCellStyles}>
            {!row?.webSupported ? (
              <Text>-</Text>
            ) : row?.connected ? (
              <span css={statusBadgeStyles('success')}>
                <CheckIcon size={14} />
                Yes
              </span>
            ) : (
              <span css={statusBadgeStyles('critical')}>
                <XIcon size={14} />
                No
              </span>
            )}
          </div>
        ),
        maxWidth: 200,
      },
    ],
    []
  );

  return (
    <section css={containerStyles}>
      <Heading level={3} css={headingStyles}>
        Payer Mix Details
      </Heading>
      <Text size="medium" color="subdued" css={subtitleStyles}>
        Detailed breakdown of payer appointments and connection status
      </Text>
      <Table
        tableInstanceId="payer-mix-details-table"
        colConfig={columns}
        data={sortedPayerMixDetails}
        isPaginated
        clientPaginationConfig={{
          defaultRowsPerPage: 10,
          rowsPerPageOptions: [10, 25, 50],
        }}
      />
    </section>
  );
};

const containerStyles = css({
  marginTop: theme.spacing(3),
});

const headingStyles = css({
  margin: 0,
  marginBottom: theme.spacing(0.5),
});

const subtitleStyles = css({
  marginBottom: theme.spacing(2),
});

const statusCellStyles = css({
  display: 'flex',
  alignItems: 'center',
});

const statusBadgeStyles = (variant: 'success' | 'neutral' | 'critical') =>
  css({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
    borderRadius: theme.borderRadius.small,
    fontSize: theme.fontSize(14),
    fontWeight: 500,
    backgroundColor:
      variant === 'success'
        ? theme.colors.success5
        : variant === 'critical'
        ? theme.colors.critical5
        : theme.colors.neutral10,
    color:
      variant === 'success'
        ? theme.colors.success40
        : variant === 'critical'
        ? theme.colors.critical40
        : theme.colors.neutral70,
  });
